import type { MediaAgentRecoveryV1 } from '@/types/player/perform-control-v1.types.ts';

/** Mirrors `MEDIA_AGENT_RECOVERY_UI_DELAY_MS` on the event service — blips must not flash the banner. */
export const MEDIA_AGENT_RECOVERY_UI_DELAY_MS = 3000;

export type MediaAgentRecoveryCopy = {
  titleKey: string;
  titleFallback: string;
  bodyKey: string;
  bodyFallback: string;
};

const RECONNECTING_CONTROL: MediaAgentRecoveryCopy = {
  titleKey: 'flows.detail.control.mediaAgentRecovery.reconnectingControl.title',
  titleFallback: 'Reconnecting to the presentation computer.',
  bodyKey: 'flows.detail.control.mediaAgentRecovery.reconnectingControl.body',
  bodyFallback:
    'The audience may still see the current slide. Slide buttons will work again once we are back — or we will switch to backup slides shortly.',
};

const RECONNECTING_PLAYOUT: MediaAgentRecoveryCopy = {
  titleKey: 'flows.detail.control.mediaAgentRecovery.reconnectingPlayout.title',
  titleFallback: 'The live slide feed was interrupted.',
  bodyKey: 'flows.detail.control.mediaAgentRecovery.reconnectingPlayout.body',
  bodyFallback: 'We are trying to restore it. Backup slides will take over if needed.',
};

const BACKUP_SLIDES: MediaAgentRecoveryCopy = {
  titleKey: 'flows.detail.control.mediaAgentRecovery.backupSlides.title',
  titleFallback: 'PowerPoint publisher is unavailable',
  bodyKey: 'flows.detail.control.mediaAgentRecovery.backupSlides.body',
  bodyFallback:
    'Playing static backup slides so you can keep presenting. We are restoring the publisher in the background.',
};

const SWITCHING_COMPUTER: MediaAgentRecoveryCopy = {
  titleKey: 'flows.detail.control.mediaAgentRecovery.switchingComputer.title',
  titleFallback: 'Moving to a spare presentation computer.',
  bodyKey: 'flows.detail.control.mediaAgentRecovery.switchingComputer.body',
  bodyFallback: 'Slide control may pause for a moment; we will return to the same slide.',
};

const RESTORING: MediaAgentRecoveryCopy = {
  titleKey: 'flows.detail.control.mediaAgentRecovery.restoring.title',
  titleFallback: 'Presentation computer is back.',
  bodyKey: 'flows.detail.control.mediaAgentRecovery.restoring.body',
  bodyFallback: 'Returning to the live feed…',
};

/** Episode is worth showing to the operator (an explicit `phase: 'none'` clear is not). */
export function isMediaAgentRecoveryEngaged(
  recovery: MediaAgentRecoveryV1 | undefined,
): recovery is MediaAgentRecoveryV1 {
  return Boolean(recovery?.active) && recovery?.phase !== 'none';
}

/**
 * Epoch ms at which the operator UI may appear, derived from the server episode start so all
 * clients agree even when they join mid-episode. Falls back to `now + delay` for an unusable `since`.
 */
export function resolveMediaAgentRecoveryShowAtMs(
  recovery: MediaAgentRecoveryV1,
  nowMs: number,
  delayMs: number = MEDIA_AGENT_RECOVERY_UI_DELAY_MS,
): number {
  const sinceMs = Date.parse(recovery.since);
  const waitMs =
    recovery.phase === 'backup_slides' || recovery.phase === 'switching_computer' || recovery.phase === 'restoring'
      ? 0
      : delayMs;
  if (Number.isNaN(sinceMs)) {
    return nowMs + waitMs;
  }
  return sinceMs + waitMs;
}

/** Control loss blocks the operator entirely, so it wins the `reconnecting` variant. */
export function resolveMediaAgentRecoveryCopy(recovery: MediaAgentRecoveryV1): MediaAgentRecoveryCopy {
  switch (recovery.phase) {
    case 'backup_slides':
      return BACKUP_SLIDES;
    case 'switching_computer':
      return SWITCHING_COMPUTER;
    case 'restoring':
      return RESTORING;
    default:
      return recovery.controlHealthy ? RECONNECTING_PLAYOUT : RECONNECTING_CONTROL;
  }
}

/** True while slides are served from renditions instead of the presentation computer. */
export function isMediaAgentBackupSlidesActive(playState?: { backupSlides?: boolean } | null): boolean {
  return playState?.backupSlides === true;
}

/** Official episode only — leftover stills must not invent a second mode. */
export function resolveMediaAgentRecoveryForUi(
  recovery: MediaAgentRecoveryV1 | undefined,
): MediaAgentRecoveryV1 | undefined {
  if (isMediaAgentRecoveryEngaged(recovery)) {
    return recovery;
  }
  return undefined;
}
